import mixpanel from "mixpanel-browser";

type EventProps = Record<string, unknown>;

interface IdentifyPayload {
  uid: string;
  email?: string | null;
  signupDateIso?: string | null;
  displayName?: string | null;
}

let initialized = false;
let enabled = false;
let lastIdentifiedUid: string | null = null;

function shouldEnable(): boolean {
  const token = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
  if (!token) return false;

  const forceEnable =
    String(import.meta.env.VITE_ANALYTICS_FORCE_ENABLE ?? "").toLowerCase() === "true";
  if (forceEnable) return true;

  return Boolean(import.meta.env.PROD);
}

function safeCall(action: () => void): void {
  if (!enabled) return;
  try {
    action();
  } catch (error: unknown) {
    console.warn("Analytics call failed:", error);
  }
}

export function initializeAnalytics(): void {
  if (initialized) return;
  initialized = true;

  if (!shouldEnable()) {
    enabled = false;
    return;
  }

  const token = import.meta.env.VITE_MIXPANEL_TOKEN as string;
  try {
    mixpanel.init(token, {
      debug: false,
      track_pageview: false,
      persistence: "localStorage",
      ignore_dnt: false,
    });
    mixpanel.register({ platform: "web" });
    enabled = true;
  } catch (error: unknown) {
    enabled = false;
    console.warn("Mixpanel init failed:", error);
  }
}

export function trackEvent(name: string, properties?: EventProps): void {
  safeCall(() => {
    mixpanel.track(name, properties as Record<string, unknown> | undefined);
  });
}

export function identifyUser(payload: IdentifyPayload): void {
  safeCall(() => {
    if (lastIdentifiedUid === payload.uid) return;
    mixpanel.identify(payload.uid);
    lastIdentifiedUid = payload.uid;

    const peopleProps: Record<string, unknown> = {
      $distinct_id: payload.uid,
    };
    if (payload.email) peopleProps.$email = payload.email;
    if (payload.displayName) peopleProps.$name = payload.displayName;
    else if (payload.email) peopleProps.$name = payload.email.split("@")[0];
    if (payload.signupDateIso) peopleProps.signup_date_iso = payload.signupDateIso;

    mixpanel.people.set(peopleProps);
  });
}

export function setUserProperties(props: EventProps): void {
  safeCall(() => {
    mixpanel.people.set(props as Record<string, unknown>);
  });
}

export function resetAnalytics(): void {
  safeCall(() => {
    mixpanel.reset();
    mixpanel.register({ platform: "web" });
  });
  lastIdentifiedUid = null;
}
